import { apiClient } from '@shared/api';
import type { ApiResponse } from '@shared/types';
import type {
  AnnouncementDto,
  AnnouncementListItemDto,
  CreateAnnouncementDto,
  UpdateAnnouncementDto,
} from '../types';

class AnnouncementService {
  // `background`: the app shell fetches this on EVERY load for every user with announcement.public_view,
  // so a failure must stay silent (86d3va61r). It matters now that /api/announcements is behind the ANNT
  // module gate — without this, switching ANNT off would toast every user on every page load. The store
  // keeps the message in activeError; the Home page just shows no announcements.
  async getActive(): Promise<AnnouncementDto[]> {
    const { data } = await apiClient.get<ApiResponse<AnnouncementDto[]>>(
      '/announcements/active',
      { background: true }
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch active announcements');
  }

  async getAll(): Promise<AnnouncementListItemDto[]> {
    const { data } = await apiClient.get<ApiResponse<AnnouncementListItemDto[]>>(
      '/announcements'
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch announcements');
  }

  async getById(id: number): Promise<AnnouncementDto> {
    const { data } = await apiClient.get<ApiResponse<AnnouncementDto>>(
      `/announcements/${id}`
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch announcement');
  }

  async create(payload: CreateAnnouncementDto): Promise<AnnouncementDto> {
    const { data } = await apiClient.post<ApiResponse<AnnouncementDto>>(
      '/announcements',
      payload
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to create announcement');
  }

  async update(id: number, payload: UpdateAnnouncementDto): Promise<AnnouncementDto> {
    const { data } = await apiClient.put<ApiResponse<AnnouncementDto>>(
      `/announcements/${id}`,
      payload
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to update announcement');
  }

  async delete(id: number): Promise<void> {
    const { data } = await apiClient.delete<ApiResponse<object>>(
      `/announcements/${id}`
    );
    if (!data.success) {
      throw new Error(data.message || 'Failed to delete announcement');
    }
  }
}

export const announcementService = new AnnouncementService();
